import { ProductBehavior as ProductBehaviorEnum } from '@up2stay/up2promo-api-client';
import type {
  ProductBehavior,
  ProductDto,
  ReputationInsightDto,
} from '@up2stay/up2promo-api-client';

import { shellPaths } from '@/core/router/app-paths';

import type {
  DashboardHealthMix,
  DashboardHealthStatus,
  DashboardLinkTargetRow,
  DashboardPeriodChange,
  DashboardProductMetrics,
  DashboardProfileViewMetrics,
  DashboardScanPoint,
  DashboardTopBusinessCardRow,
  DashboardTopCardOwner,
} from './dashboard-status-types';

export const HEALTH_ROW_LIMIT = 8;
export const DO_NEXT_LIMIT = 4;
export const ATTENTION_REVIEW_LIMIT = 4;
export const ATTENTION_NOTIFICATION_LIMIT = 5;
export const LIVE_SCAN_LIMIT = 8;
export const TOP_BUSINESS_CARDS_LIMIT = 5;
export const LINK_TARGETS_LIMIT = 5;

function usageBucketDay(item: {
  _id?: string;
  timestamp?: string;
}): string | null {
  // Time-series API returns the bucket start in `_id` (ISO day), not `timestamp`.
  const raw = item._id ?? item.timestamp;
  if (!raw) return null;
  const day = raw.slice(0, 10);
  return /^\d{4}-\d{2}-\d{2}$/.test(day) ? day : null;
}

export function buildScanSeries(
  raw: { _id?: string; timestamp?: string; count?: number }[],
  days = 30,
): DashboardScanPoint[] {
  const byDay = new Map<string, number>();
  for (const item of raw) {
    const day = usageBucketDay(item);
    if (!day) continue;
    byDay.set(day, (byDay.get(day) ?? 0) + (item.count ?? 0));
  }

  const end = new Date();
  end.setUTCHours(0, 0, 0, 0);
  const points: DashboardScanPoint[] = [];
  for (let i = days - 1; i >= 0; i -= 1) {
    const d = new Date(end);
    d.setUTCDate(d.getUTCDate() - i);
    const key = d.toISOString().slice(0, 10);
    points.push({ date: key, scans: byDay.get(key) ?? 0 });
  }
  return points;
}

/** Current window + previous equal window from a 2×-length daily series. */
export function splitCurrentAndPreviousSeries(
  seriesDouble: DashboardScanPoint[],
  rangeDays: number,
): { current: DashboardScanPoint[]; previous: DashboardScanPoint[] } {
  if (seriesDouble.length < rangeDays * 2) {
    const current = buildScanSeries(
      seriesDouble.map((p) => ({ _id: p.date, count: p.scans })),
      rangeDays,
    );
    return {
      current,
      previous: buildScanSeries([], rangeDays),
    };
  }
  return {
    previous: seriesDouble.slice(0, rangeDays),
    current: seriesDouble.slice(rangeDays),
  };
}

export function sumScans(points: DashboardScanPoint[]): number {
  return points.reduce((sum, point) => sum + point.scans, 0);
}

/** Period-over-period change for KPI / chart footers. */
export function periodOverPeriodChange(
  current: number,
  previous: number,
): DashboardPeriodChange {
  if (previous === 0) {
    if (current === 0) {
      return { percent: 0, direction: 'flat' };
    }
    return { percent: null, direction: 'new' };
  }
  const percent = ((current - previous) / previous) * 100;
  if (Math.abs(percent) < 0.05) {
    return { percent: 0, direction: 'flat' };
  }
  return {
    percent,
    direction: percent > 0 ? 'up' : 'down',
  };
}

export const EMPTY_PRODUCT_METRICS: DashboardProductMetrics = {
  totalProducts: 0,
  totalScans: 0,
  previousPeriodScans: 0,
  periodChange: { percent: 0, direction: 'flat' },
  activeProductsCount: 0,
  scanSeries: buildScanSeries([], 30),
  previousScanSeries: buildScanSeries([], 30),
  scanSources: [],
  linkTargets: [],
  topBusinessCards: [],
};

export const EMPTY_PROFILE_VIEW_METRICS: DashboardProfileViewMetrics = {
  totalViews: 0,
  previousPeriodViews: 0,
  periodChange: { percent: 0, direction: 'flat' },
  sources: [],
  viewSeries: [],
  previousViewSeries: [],
  viaProductCount: 0,
};

/**
 * Align previous-period daily counts onto the current window’s dates
 * (same day-of-range overlay for chart comparison).
 */
export function overlayPreviousPeriodScans(
  current: DashboardScanPoint[],
  previous: DashboardScanPoint[],
): Array<DashboardScanPoint & { previousScans: number }> {
  return current.map((point, index) => ({
    ...point,
    previousScans: previous[index]?.scans ?? 0,
  }));
}

/** Downsample a daily series into sparkline Y values (0–1).
 * Uses max-per-bucket so a single peak day (e.g. mid-July) stays visible —
 * averages dilute spikes and make late sustained activity look like the peak.
 */
export function sparklineValues(
  points: ReadonlyArray<{ value?: number; scans?: number }>,
  buckets = 12,
): number[] {
  if (points.length === 0) return [];
  const daily = points.map((p) => p.value ?? p.scans ?? 0);
  if (daily.every((v) => v === 0)) {
    return Array.from(
      { length: Math.min(buckets, daily.length) || buckets },
      () => 0,
    );
  }
  const chunk = Math.max(1, Math.ceil(daily.length / buckets));
  const values: number[] = [];
  for (let i = 0; i < buckets; i += 1) {
    const start = i * chunk;
    if (start >= daily.length) break;
    const slice = daily.slice(start, start + chunk);
    values.push(Math.max(...slice, 0));
  }
  const max = Math.max(...values, 1);
  return values.map((v) => v / max);
}

/**
 * Busiest day across product taps and profile views. The card is „Taps &
 * Profilaufrufe“, so profile-only workspaces must not read „—“ (DEV-542).
 */
export function strongestActivityDay(
  scanSeries: DashboardScanPoint[],
  viewSeries: { date: string; views: number }[],
): { date: string; scans: number; views: number } | null {
  const byDate = new Map<string, { scans: number; views: number }>();
  for (const point of scanSeries) {
    const entry = byDate.get(point.date) ?? { scans: 0, views: 0 };
    entry.scans += point.scans;
    byDate.set(point.date, entry);
  }
  for (const point of viewSeries) {
    const entry = byDate.get(point.date) ?? { scans: 0, views: 0 };
    entry.views += point.views;
    byDate.set(point.date, entry);
  }
  let best: { date: string; scans: number; views: number } | null = null;
  const days = [...byDate].sort(([a], [b]) => a.localeCompare(b));
  for (const [date, entry] of days) {
    const total = entry.scans + entry.views;
    if (total === 0) continue;
    // Earliest day wins a tie, like the tap-only version did.
    if (!best || total > best.scans + best.views) best = { date, ...entry };
  }
  return best;
}

export function averageScansPerDay(
  totalScans: number,
  rangeDays: number,
): number {
  if (rangeDays <= 0) return 0;
  return totalScans / rangeDays;
}

/** Where scans lead: the target kind, else the factory behaviour, else URL. */
function resolveTargetKind(
  product: Pick<ProductDto, 'target' | 'item'>,
): ProductBehavior {
  return (
    product.target?.kind ?? product.item.behavior ?? ProductBehaviorEnum.URL
  );
}

const LINK_TARGET_TAG: Record<
  ProductBehavior,
  { tag: string; tagBg: string; tagFg: string }
> = {
  [ProductBehaviorEnum.PROFILE]: {
    tag: 'BC',
    tagBg: '#E8EDFD',
    tagFg: '#1C3FB8',
  },
  [ProductBehaviorEnum.GOOGLE_REVIEW]: {
    tag: 'G',
    tagBg: '#FEF3D7',
    tagFg: '#8A5A00',
  },
  [ProductBehaviorEnum.URL]: {
    tag: 'WW',
    tagBg: '#EDEBE6',
    tagFg: '#17181C',
  },
  [ProductBehaviorEnum.INSTAGRAM]: {
    tag: 'IG',
    tagBg: '#FCE7F3',
    tagFg: '#9D174D',
  },
  [ProductBehaviorEnum.FACEBOOK]: {
    tag: 'FB',
    tagBg: '#E0EEF9',
    tagFg: '#0B5394',
  },
  [ProductBehaviorEnum.TIKTOK]: {
    tag: 'TT',
    tagBg: '#EDEBE6',
    tagFg: '#17181C',
  },
  [ProductBehaviorEnum.MENU]: {
    tag: 'ME',
    tagBg: '#E7F8EE',
    tagFg: '#12945A',
  },
};

/** Map Status Board BFF link-target rows onto card chrome (tag + colours). */
export function decorateStatusBoardLinkTargets(
  rows: Array<{
    type: ProductBehavior;
    productCount: number;
    scans: number;
    share: number;
  }>,
  typeLabels: LinkTargetTypeLabels = defaultLinkTargetTypeLabels(),
): DashboardLinkTargetRow[] {
  return rows.map((row) => {
    const chrome =
      LINK_TARGET_TAG[row.type] ?? LINK_TARGET_TAG[ProductBehaviorEnum.URL];
    return {
      id: row.type,
      type: row.type,
      tag: chrome.tag,
      name: typeLabels[row.type] ?? row.type,
      productCount: row.productCount,
      scans: row.scans,
      share: row.share,
      tagBg: chrome.tagBg,
      tagFg: chrome.tagFg,
    };
  });
}

/** Map Status Board BFF top-card rows onto board card props. */
export function decorateStatusBoardTopCards(
  rows: Array<{
    userId: string;
    name: string;
    initials: string;
    avatarUrl?: string | null;
    meta?: string | null;
    taps: number;
  }>,
  tapsLabel: (count: number) => string = (count) => String(count),
): DashboardTopBusinessCardRow[] {
  return rows.map((row, index) => ({
    id: row.userId,
    initials: row.initials || initialsFromLabel(row.name),
    name: row.name,
    meta: row.meta?.trim() || '',
    avatarUrl: row.avatarUrl ?? null,
    avatarTone: (index % 2 === 0 ? 'warm' : 'cool') as 'warm' | 'cool',
    taps: row.taps,
    tapsLabel: tapsLabel(row.taps),
  }));
}

export type LinkTargetTypeLabels = Record<ProductBehavior, string>;

/** Aggregate configured product routing types for Link targets. */
export function buildLinkTargetRows(
  products: ProductDto[],
  typeLabels: LinkTargetTypeLabels,
  limit = LINK_TARGETS_LIMIT,
): DashboardLinkTargetRow[] {
  if (products.length === 0) return [];

  const byType = new Map<
    ProductBehavior,
    { productCount: number; scans: number }
  >();
  for (const product of products) {
    const type = resolveTargetKind(product);
    const current = byType.get(type) ?? { productCount: 0, scans: 0 };
    current.productCount += 1;
    current.scans += product.scanCount ?? 0;
    byType.set(type, current);
  }

  const totalProducts = products.length;
  const rows: DashboardLinkTargetRow[] = [...byType.entries()]
    .map(([type, stats]) => {
      const chrome = LINK_TARGET_TAG[type];
      return {
        id: type,
        type,
        tag: chrome.tag,
        name: typeLabels[type],
        productCount: stats.productCount,
        scans: stats.scans,
        share:
          totalProducts > 0 ? (stats.productCount / totalProducts) * 100 : 0,
        tagBg: chrome.tagBg,
        tagFg: chrome.tagFg,
      };
    })
    .sort((a, b) => b.productCount - a.productCount || b.scans - a.scans)
    .slice(0, limit);

  return rows;
}

/**
 * Merchant-facing BC / digital-profile products only.
 * (PROFILE target or factory behaviour).
 */
export function isProfileBusinessCard(
  product: Pick<ProductDto, 'target' | 'item'>,
): boolean {
  return (
    product.target?.kind === ProductBehaviorEnum.PROFILE ||
    product.item.behavior === ProductBehaviorEnum.PROFILE
  );
}

/**
 * Top profile owners by aggregated BC / PROFILE product taps.
 * Ranks people (profile owners), not orphan product rows.
 * `scanCount` must already match the selected Status Board range.
 */
export function buildTopBusinessCardRows(
  products: ProductDto[],
  tapsLabel: (count: number) => string,
  ownersByUserId: ReadonlyMap<string, DashboardTopCardOwner> = new Map(),
  limit = TOP_BUSINESS_CARDS_LIMIT,
): DashboardTopBusinessCardRow[] {
  const tapsByOwner = new Map<string, number>();
  for (const product of products) {
    if (!isProfileBusinessCard(product)) continue;
    const userId = product.target?.userId?.trim();
    if (!userId) continue;
    tapsByOwner.set(
      userId,
      (tapsByOwner.get(userId) ?? 0) + (product.scanCount ?? 0),
    );
  }

  const ranked = [...tapsByOwner.entries()]
    .filter(([, taps]) => taps > 0)
    .sort((a, b) => b[1] - a[1])
    .slice(0, limit);

  return ranked.map(([userId, taps], index) => {
    const owner = ownersByUserId.get(userId);
    const name = owner?.name?.trim() || userId;
    return {
      id: userId,
      initials: owner?.initials?.trim() || initialsFromLabel(name),
      name,
      meta: owner?.meta?.trim() || '',
      avatarUrl: owner?.avatarUrl ?? null,
      avatarTone: (index % 2 === 0 ? 'warm' : 'cool') as 'warm' | 'cool',
      taps,
      tapsLabel: tapsLabel(taps),
    };
  });
}

function initialsFromLabel(label: string): string {
  const parts = label.trim().split(/\s+/).filter(Boolean);
  if (parts.length === 0) return '?';
  if (parts.length === 1) {
    return parts[0]!.slice(0, 2).toUpperCase();
  }
  return `${parts[0]!.charAt(0)}${parts[1]!.charAt(0)}`.toUpperCase();
}

/** Shared interrupt rank: hero CTA and Do next must use the same order. */
export function doNextRank(id: string): number {
  switch (id) {
    case 'new-leads':
      return 5;
    case 'open-feedback':
      return 8;
    case 'critical-reviews':
      return 10;
    case 'sync-failed':
      return 20;
    case 'setup-reconnect':
      return 30;
    case 'setup-connect':
      return 40;
    case 'setup-link':
      return 50;
    case 'zero-reviews':
      return 55;
    case 'add-location':
      return 70;
    case 'create-profile':
      return 80;
    case 'add-product':
      return 90;
    default:
      if (id.startsWith('insight-')) return 200;
      if (id.startsWith('summary-')) return 210;
      return 150;
  }
}

export function healthStatusBucket(
  status: DashboardHealthStatus,
): keyof Omit<DashboardHealthMix, 'total'> {
  switch (status) {
    case 'critical':
    case 'sync':
      return 'critical';
    case 'warning':
    case 'setup':
      return 'attention';
    case 'ok':
      return 'ok';
    default:
      return 'unknown';
  }
}

export function formatInsightText(
  templates: Record<string, string>,
  insight: ReputationInsightDto,
): string {
  const template = templates[insight.key];
  if (!template) return insight.key;
  return template.replace(/\{(\w+)\}/g, (_, key: string) =>
    String(insight.params[key] ?? ''),
  );
}

export function insightHref(insight: ReputationInsightDto): string {
  const action = insight.action;
  if (!action) return shellPaths.reputationDashboard;
  if (action.kind === 'inbox') {
    const params = new URLSearchParams();
    if (action.aspect) params.set('aspect', action.aspect);
    if (action.sentiment) params.set('sentiment', action.sentiment);
    if (action.locationId) params.set('locationId', action.locationId);
    const query = params.toString();
    return query
      ? `${shellPaths.reputationInbox}?${query}`
      : shellPaths.reputationInbox;
  }
  if (action.kind === 'location' && action.locationId) {
    return shellPaths.moreWorkspaceLocationDetail(action.locationId);
  }
  if (action.kind === 'analytics') {
    return shellPaths.reputationAnalytics;
  }
  return shellPaths.reputationDashboard;
}

export function mergeRankedLocations(
  top: {
    locationId: string;
    locationName: string;
    averageRating: number;
    totalReviews: number;
  }[],
  bottom: {
    locationId: string;
    locationName: string;
    averageRating: number;
    totalReviews: number;
  }[],
) {
  const byId = new Map<string, (typeof top)[number]>();
  for (const row of [...bottom, ...top]) {
    byId.set(row.locationId, row);
  }
  return [...byId.values()];
}

export function defaultLinkTargetTypeLabels(): LinkTargetTypeLabels {
  return {
    [ProductBehaviorEnum.PROFILE]: 'Digital profile',
    [ProductBehaviorEnum.GOOGLE_REVIEW]: 'Google review',
    [ProductBehaviorEnum.URL]: 'Custom link',
    [ProductBehaviorEnum.INSTAGRAM]: 'Instagram',
    [ProductBehaviorEnum.FACEBOOK]: 'Facebook',
    [ProductBehaviorEnum.TIKTOK]: 'TikTok',
    [ProductBehaviorEnum.MENU]: 'Menu',
  };
}
